export const SETTINGS_KEY = 'tankzone:settings:v1';
export const SCORES_KEY = 'tankzone:scores:v1';

export type ReducedMotionSetting = 'system' | 'reduce' | 'full';
export type TouchMode = 'auto' | 'always' | 'never';
export type KeyboardSetting = 'faithful' | 'standard';

export interface Settings {
  muted: boolean;
  volume: number;
  reducedMotion: ReducedMotionSetting;
  keyboardPreset: KeyboardSetting;
  touchMode: TouchMode;
}

export interface ScoreEntry {
  initials: string;
  score: number;
  timestamp: number;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = {
  muted: false,
  volume: 0.72,
  reducedMotion: 'system',
  keyboardPreset: 'faithful',
  touchMode: 'auto',
};

export const DEFAULT_SCORES: readonly ScoreEntry[] = [
  { initials: 'VTR', score: 25_000, timestamp: 1 },
  { initials: 'ACE', score: 18_000, timestamp: 2 },
  { initials: 'ION', score: 12_000, timestamp: 3 },
  { initials: 'HEX', score: 9_000, timestamp: 4 },
  { initials: 'RAY', score: 7_000, timestamp: 5 },
  { initials: 'NIX', score: 5_000, timestamp: 6 },
  { initials: 'ORB', score: 3_000, timestamp: 7 },
  { initials: 'ZED', score: 1_000, timestamp: 8 },
];

const INITIALS_PATTERN = /[^A-Z0-9.-]/g;

export class TankzoneStorage {
  public constructor(private readonly storage: Storage | null) {}

  public loadSettings(): Settings {
    const value = this.read(SETTINGS_KEY);
    if (!isObject(value)) return { ...DEFAULT_SETTINGS };
    return {
      muted: typeof value.muted === 'boolean' ? value.muted : DEFAULT_SETTINGS.muted,
      volume: typeof value.volume === 'number' && Number.isFinite(value.volume)
        ? Math.max(0, Math.min(1, value.volume))
        : DEFAULT_SETTINGS.volume,
      reducedMotion: isOneOf(value.reducedMotion, ['system', 'reduce', 'full'])
        ? value.reducedMotion
        : DEFAULT_SETTINGS.reducedMotion,
      keyboardPreset: isOneOf(value.keyboardPreset, ['faithful', 'standard'])
        ? value.keyboardPreset
        : DEFAULT_SETTINGS.keyboardPreset,
      touchMode: isOneOf(value.touchMode, ['auto', 'always', 'never'])
        ? value.touchMode
        : DEFAULT_SETTINGS.touchMode,
    };
  }

  public saveSettings(settings: Settings): void {
    this.write(SETTINGS_KEY, settings);
  }

  public loadScores(): ScoreEntry[] {
    const value = this.read(SCORES_KEY);
    if (!Array.isArray(value)) return DEFAULT_SCORES.map((entry) => ({ ...entry }));
    const entries = value.map(parseScore).filter((entry) => entry !== null);
    return sortScores(entries).slice(0, 8);
  }

  public qualifies(score: number): boolean {
    const scores = this.loadScores();
    return score > 0 && (scores.length < 8 || score > (scores[7]?.score ?? 0));
  }

  public addScore(initials: string, score: number, timestamp = Date.now()): ScoreEntry[] {
    const entry = parseScore({ initials, score, timestamp });
    const scores = this.loadScores();
    if (!entry) return scores;
    const next = sortScores([...scores, entry]).slice(0, 8);
    this.write(SCORES_KEY, next);
    return next;
  }

  public clearScores(): ScoreEntry[] {
    try {
      this.storage?.removeItem(SCORES_KEY);
    } catch {
      // Storage is optional; defaults remain usable in memory.
    }
    return DEFAULT_SCORES.map((entry) => ({ ...entry }));
  }

  private read(key: string): unknown {
    try {
      const raw = this.storage?.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: unknown): void {
    try {
      this.storage?.setItem(key, JSON.stringify(value));
    } catch {
      // Quota, privacy, and unavailable-storage errors must never block play.
    }
  }
}

export function sanitizeInitials(value: string): string {
  return value.toUpperCase().replace(INITIALS_PATTERN, '').slice(0, 3).padEnd(3, '.');
}

function parseScore(value: unknown): ScoreEntry | null {
  if (!isObject(value)) return null;
  if (typeof value.initials !== 'string' || typeof value.score !== 'number' || typeof value.timestamp !== 'number') {
    return null;
  }
  if (!Number.isFinite(value.score) || !Number.isFinite(value.timestamp)) return null;
  return {
    initials: sanitizeInitials(value.initials),
    score: Math.max(0, Math.min(99_999_999, Math.floor(value.score))),
    timestamp: Math.max(0, Math.floor(value.timestamp)),
  };
}

function sortScores(scores: ScoreEntry[]): ScoreEntry[] {
  return [...scores].sort((left, right) => right.score - left.score || left.timestamp - right.timestamp);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isOneOf<T extends string>(value: unknown, choices: readonly T[]): value is T {
  return typeof value === 'string' && choices.includes(value as T);
}
